import { Box, Button, Image, Notification } from '@wix/design-system'
import { useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import React, { FC, useEffect } from 'react'

import { usePageLogger } from '../../../bi/mfi'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { WixMobileBackButton } from '../../reusable/WixMobileBackButton/WixMobileBackButton'
import { WixMobileContentWrapper } from '../../reusable/WixMobileContentWrapper/WixMobileContentWrapper'
import { WixMobileDescription } from '../../reusable/WixMobileDescription/WixMobileDescription'
import { WixMobileFooter } from '../../reusable/WixMobileFooter/WixMobileFooter'
import { WixMobileHeader } from '../../reusable/WixMobileHeader/WixMobileHeader'
import { WixMobileHeading } from '../../reusable/WixMobileHeading/WixMobileHeading'
import { WixMobilePage } from '../../reusable/WixMobilePage/WixMobilePage'
import { TWixMobileGetOwnerApp } from './typings/WixMobileGetOwnerApp.content'
import { wixMobileGetOwnerAppDataHooks as dataHooks } from './WixMobileGetOwnerApp.dataHooks'

export const WixMobileGetOwnerApp: FC = () => {
  const { content } = useContent<TWixMobileGetOwnerApp>()
  const { provisionedMetaSiteId } = useStorageContext()
  const next = useNext()
  const [pageLoading] = usePageLoading()
  const pageLogger = usePageLogger()

  const handleNext = () => {
    if (pageLoading) {
      return
    }
    void pageLogger.answer({ answer: 'get_owner_app' })
    next()
  }

  const handleBack = () => {
    if (pageLoading) {
      return
    }
    void pageLogger.back()
    window.history.back()
  }

  const handleOptionClick = (optionKey: string) => {
    if (pageLoading) {
      return
    }

    void pageLogger.answer({ answer: optionKey })
    next(
      { [content.question.questionKey]: { values: [optionKey] } },
      {
        metaSiteId: provisionedMetaSiteId,
      },
    )
  }

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  return (
    <WixMobilePage>
      <WixMobileHeader leftContent={<WixMobileBackButton onBack={handleBack} dataHook={dataHooks.back()} />} />
      {content.successMessage && (
        <Notification theme="success" dataHook={dataHooks.successMesssage()} type="local" show>
          <Notification.TextLabel>{content.successMessage}</Notification.TextLabel>
        </Notification>
      )}
      <WixMobileContentWrapper>
        {content.illustrationImageUrl ? (
          <Box align="center" verticalAlign="middle" paddingTop="36px" paddingBottom="12px">
            <Image
              borderRadius={0}
              width="120px"
              height="120px"
              src={content.illustrationImageUrl}
              dataHook={dataHooks.illustrationImage()}
              transparent
            />
          </Box>
        ) : (
          <Box height="52px" />
        )}
        <WixMobileHeading dataHook={dataHooks.title()}>{content.title}</WixMobileHeading>
        <WixMobileDescription dataHook={dataHooks.description()}>{content.description}</WixMobileDescription>
        {content.question.options.length > 0 && (
          <Box gap="SP2" direction="vertical" paddingTop="SP1">
            {content.question.options.map(({ optionKey, label }) => (
              <Button
                size="large"
                priority="secondary"
                fullWidth
                onClick={() => handleOptionClick(optionKey)}
                dataHook={dataHooks.optionButton({ optionKey })}
                key={optionKey}
              >
                {label}
              </Button>
            ))}
          </Box>
        )}
      </WixMobileContentWrapper>
      {content.next && (
        <WixMobileFooter>
          <Button size="large" fullWidth onClick={handleNext} dataHook={dataHooks.next({})}>
            {content.next}
          </Button>
        </WixMobileFooter>
      )}
    </WixMobilePage>
  )
}
